import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { PersonalizedScenarioDraft } from 'helper/personalized';
import { selectFluxPersonalizedFormShowInstructions } from 'redux/reducers/fluxPersonalizedFormReducer';

type Scenarios = Record<string, PersonalizedScenarioDraft>;

interface ScenariosDraft {
  scenarios: Scenarios,
  setScenarios: (scenarios: Scenarios) => void,
  scenarioCount: number,
  setScenarioCount: (count: number) => void,
}

const initialScenarios = (): Scenarios => ({ 0: { name: '', blocks: [] } });

const ScenariosDraftContext = createContext<ScenariosDraft | null>(null);

// Os cenários (com Date nos blocos) ficam no wrapper da rota, e não no construtor:
// cruzar o breakpoint de 768px (girar o celular, redimensionar) troca a árvore do
// wrapper e remontava o construtor, zerando os cenários com o horizonte já travado.
// Voltam ao inicial quando o form volta às instruções (cancelar/enviar fazem reset).
export function ScenariosDraftProvider({ children }: { children: React.ReactNode }) {
  const showInstructions = useSelector(selectFluxPersonalizedFormShowInstructions);
  const [scenarios, setScenarios] = useState<Scenarios>(initialScenarios);
  const [scenarioCount, setScenarioCount] = useState(1);

  useEffect(() => {
    if (showInstructions) {
      setScenarios(initialScenarios());
      setScenarioCount(1);
    }
  }, [showInstructions]);

  const value = useMemo(() => ({
    scenarios, setScenarios, scenarioCount, setScenarioCount,
  }), [scenarios, scenarioCount]);

  return (
    <ScenariosDraftContext.Provider value={value}>
      {children}
    </ScenariosDraftContext.Provider>
  );
}

// Sem provider (testes, uso isolado), o estado fica local como antes.
export default function useScenariosDraft(): ScenariosDraft {
  const context = useContext(ScenariosDraftContext);
  const [scenarios, setScenarios] = useState<Scenarios>(initialScenarios);
  const [scenarioCount, setScenarioCount] = useState(1);
  return context ?? {
    scenarios, setScenarios, scenarioCount, setScenarioCount,
  };
}
